import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Alert, Badge, Button, Card, Spinner } from "react-bootstrap";
import {
  ArrowRight,
  Coins,
  Gift,
  Keyboard,
  Phone,
  ScanLine,
  ShieldCheck,
  User,
} from "lucide-react";

import Navbar from "../components/NavbarM";

import { getMemberByBarcode } from "../services/memberService";

import "./ScanCustomerPage.css";

function ScanCustomerPage() {
  const navigate = useNavigate();

  const [barcodeInput, setBarcodeInput] = useState("");
  const [lastScannedRaw, setLastScannedRaw] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  async function handleScan(rawValue) {
    const value = rawValue.trim();

    if (!value) return;

    setLastScannedRaw(value);
    setError("");
    setResult(null);
    setLoading(true);

    try {
      const response = await getMemberByBarcode(value);
      setResult(response);
    } catch (scanError) {
      console.error(scanError);

      setError(
        scanError.message ||
          "This barcode could not be recognized. Please try again."
      );
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(event) {
    if (event.key !== "Enter") return;

    event.preventDefault();

    const value = barcodeInput;
    setBarcodeInput("");
    handleScan(value);
  }

  function handleScanAnother() {
    setResult(null);
    setError("");
    setBarcodeInput("");
    setLastScannedRaw("");
    inputRef.current?.focus();
  }

  return (
    <>
      <Navbar />

      <div className="scan-page">
        <div className="scan-container">
          <section className="scan-header">
            <div>
              <div className="scan-pill">
                <ShieldCheck size={18} />
                Secure Code Scanner
              </div>
              <h1>Scan Customer Code</h1>
              <p>
                Scan the PointsHub code on the customer's phone to earn or
                redeem rewards.
              </p>
            </div>
          </section>

          <div className="scan-grid">
            <Card className="scan-panel">
              <div className="scan-visual">
                <div className="scan-ring">
                  <ScanLine size={50} />
                </div>
                <div className="scan-line"></div>
              </div>

              <h3>Ready to Scan</h3>
              <p className="text-muted">
                Scanner input will be captured automatically below.
              </p>

              <input
                type="text"
                ref={inputRef}
                className="scan-input"
                placeholder="Scan or enter the customer's code..."
                value={barcodeInput}
                onChange={(event) => setBarcodeInput(event.target.value)}
                onKeyDown={handleKeyDown}
                disabled={loading}
              />

              <div className="scan-actions">
                <Button
                  className="scan-secondary-btn"
                  onClick={() => inputRef.current?.focus()}
                >
                  <Keyboard size={18} />
                  Enter Manually
                </Button>

                <Button
                  className="scan-secondary-btn"
                  onClick={() => {
                    const value = barcodeInput;
                    setBarcodeInput("");
                    handleScan(value);
                  }}
                  disabled={!barcodeInput || loading}
                >
                  {loading ? (
                    <>
                      <Spinner size="sm" />
                      Checking...
                    </>
                  ) : (
                    <>
                      <ScanLine size={18} />
                      Look Up
                    </>
                  )}
                </Button>
              </div>

              {lastScannedRaw && (
                <div className="scan-raw-preview">
                  <span>Last scanned value</span>
                  <p>{lastScannedRaw}</p>
                </div>
              )}

              {error && (
                <Alert variant="danger" className="mt-3 mb-0">
                  {error}
                </Alert>
              )}
            </Card>

            <Card className="scan-result-panel">
              {!result ? (
                <div className="scan-empty-state">
                  <div className="scan-empty-icon">
                    <ScanLine size={42} />
                  </div>
                  <h3>No Code Scanned</h3>
                  <p>
                    Scan a customer's PointsHub code to view their account
                    and continue to add or redeem points.
                  </p>
                </div>
              ) : (
                <>
                  <div className="scan-member-header">
                    <Badge bg="success">Customer Found</Badge>
                  </div>

                  <div className="scan-member-details">
                    <div className="scan-detail-row">
                      <div className="scan-detail-icon">
                        <User size={20} />
                      </div>
                      <div>
                        <span>Member Name</span>
                        <strong>{result.member.name}</strong>
                      </div>
                    </div>

                    <div className="scan-detail-row">
                      <div className="scan-detail-icon">
                        <Phone size={20} />
                      </div>
                      <div>
                        <span>Phone</span>
                        <strong>
                          {result.member.phoneLastFour
                            ? `Ending in ${result.member.phoneLastFour}`
                            : "Not provided"}
                        </strong>
                      </div>
                    </div>

                    <div className="scan-detail-row highlight">
                      <div className="scan-detail-icon coins">
                        <Coins size={20} />
                      </div>
                      <div>
                        <span>Balance at This Store</span>
                        <strong>
                          {Number(result.membership.points || 0).toLocaleString()}{" "}
                          pts
                        </strong>
                      </div>
                    </div>
                  </div>

                  <div className="scan-action-buttons">
                    <Button
                      className="scan-add-btn"
                      onClick={() =>
                        navigate(`/pages/add-points/${result.member.id}`, {
                          state: { lookupMethod: "scan" },
                        })
                      }
                    >
                      <ArrowRight size={19} className="me-2" />
                      Add Points
                    </Button>

                    <Button
                      className="scan-redeem-btn"
                      onClick={() =>
                        navigate(`/pages/redeem-points/${result.member.id}`)
                      }
                    >
                      <Gift size={19} className="me-2" />
                      Redeem Points
                    </Button>
                  </div>

                  <button
                    type="button"
                    className="scan-another-btn"
                    onClick={handleScanAnother}
                  >
                    Scan Another Customer
                  </button>
                </>
              )}
            </Card>
          </div>
        </div>
      </div>
    </>
  );
}

export default ScanCustomerPage;
